import React, { useState, useEffect, useCallback } from 'react'
import * as api from './service/studyService'
import { PHASE_PATH } from './StudyPage'

const HIDE_KEY = 'study_admin_hidden'

const PARTS = [
  { phase: 'screening', label: '1 screening' },
  { phase: 'main', label: '2 main' },
  { phase: 'followup', label: '3 followup' }
]

// TEST-MODE control strip: switch participant, force-advance, reset, open follow-up.
// Collapsible — hidden it leaves only a corner pill, so the participant view can be
// seen (or screenshotted) at full height. The choice persists across reloads.
export default function AdminBar ({ pid, config, onSwitch, onLogout, onRefresh }) {
  const [list, setList] = useState([])
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === '1')

  const toggle = useCallback(() => setHidden(h => {
    const next = !h
    localStorage.setItem(HIDE_KEY, next ? '1' : '0')
    return next
  }), [])

  // Keyboard escape hatch, so the strip is never unrecoverable if the corner button is missed.
  useEffect(() => {
    const onKey = (e) => {
      // Alt+Shift+T: Cmd/Ctrl+Shift+T is Chrome's "reopen closed tab" and can't be overridden.
      // e.code, not e.key — Option+Shift on macOS rewrites e.key to a dead character.
      if (e.altKey && e.shiftKey && e.code === 'KeyT') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  const refresh = useCallback(() => { api.adminList().then(setList).catch(() => {}) }, [])
  useEffect(() => { refresh() }, [refresh, pid])

  const forceAdvance = async () => { await api.adminForceAdvance(pid); onRefresh(); refresh() }
  const reset = async () => { await api.adminReset(pid); onRefresh(); refresh() }
  const openFollowup = async () => { await api.adminOpenFollowup(pid); refresh() }
  // Releases part 2 without a trip to the dashboard. The dashboard is still the real
  // control — this is the shortcut for walking the flow repeatedly.
  const release = async () => { await api.adminRelease(pid); onRefresh(); refresh() }

  // Move the server's phase pointer, THEN navigate. A plain link to /study/main during
  // part 1 gets correctly redirected back to /study/screening, because the phase URLs
  // follow the pointer instead of setting it. Full page load so nothing stale survives.
  const jump = async (phase) => {
    await api.adminSetPhase(pid, phase)
    window.location.href = PHASE_PATH[phase] || '/study'
  }

  // Collapsed: a fixed-position pill, so the strip costs no layout height.
  if (hidden) {
    return (
      <button className="study-admin__peek" onClick={toggle}
        title="Show test controls (Alt+Shift+T)" aria-label="Show test controls">▸</button>
    )
  }

  return (
    <div className="study-admin">
      <span className="study-admin__tag">TEST MODE</span>
      <label className="study-admin__switch">
        participant:
        <select value={pid} onChange={e => onSwitch(e.target.value)}>
          {/* A participant entered via invite link isn't one of the seeds, and without
              this the strip shows a seed's name while you're actually someone else —
              confusing in a walkthrough. Show whoever is really logged in. */}
          {!config.testParticipants.includes(pid) && <option value={pid}>{pid}</option>}
          {config.testParticipants.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <button onClick={forceAdvance}>force-advance ▸</button>
      <button onClick={reset}>reset ↺</button>
      <button onClick={release}>release part 2</button>
      <button onClick={openFollowup}>open follow-up</button>
      <a className="study-admin__link" href="/study/replay">/study/replay</a>
      <span className="study-admin__jump">
        jump:
        {PARTS.map(p => (
          <button key={p.phase} onClick={() => jump(p.phase)}>{p.label}</button>
        ))}
      </span>
      <button className="study-admin__logout" onClick={onLogout}>log out</button>
      <button className="study-admin__hide" onClick={toggle} title="Hide test controls">hide ▴</button>
      <span className="study-admin__cells">
        {list.map(p => (
          <span key={p.participantId} className={p.participantId === pid ? 'is-cur' : ''}>
            {p.participantId.replace('P-TEST-', 'P')}·c{p.cell}·s{p.phase === 'followup' ? `f${p.followupStep}` : p.currentStep}
          </span>
        ))}
      </span>
    </div>
  )
}
